import { useEffect, useState } from "react";
import { login, register, errorMessage } from "@/api/auth";
import { normalizeSession, markRegisteredOnce } from "@/session/types";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";
import {
  deviceLockAvailable,
  deviceLockConfig,
  enableDeviceLock,
  disableDeviceLock,
} from "@/security/deviceLock";

type Mode = "login" | "register";

export function AuthPanel({ onSuccess }: { onSuccess: () => void }) {
  const { setSession, session } = useSession();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockReady, setLockReady] = useState(false);
  const [lockOn, setLockOn] = useState(() => !!deviceLockConfig());

  async function refreshLock() {
    setLockReady(await deviceLockAvailable());
    setLockOn(!!deviceLockConfig());
  }

  useEffect(() => {
    if (session) void refreshLock();
  }, [session]);

  async function submit() {
    setBusy(true);
    try {
      if (!/^[a-zA-Z0-9_]{3,32}$/.test(username) || password.length < 8 || password.length > 64) {
        throw new Error("USERNAME_OR_PASSWORD");
      }
      if (mode === "register" && password !== password2) {
        throw new Error("PASSWORD_MISMATCH");
      }
      const out =
        mode === "register"
          ? await register(username, password)
          : await login(username, password);
      if (mode === "register") markRegisteredOnce();
      setSession(normalizeSession(out, username));
      toast.ok(mode === "register" ? "ثبت‌نام" : "ورود");
      await refreshLock();
      onSuccess();
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onEnableLock() {
    try {
      if (!session?.username) throw new Error("SESSION");
      await enableDeviceLock(session.username, session.displayName);
      toast.ok("قفل دستگاه");
      await refreshLock();
    } catch (e) {
      toast.err(errorMessage(e));
    }
  }

  function onDisableLock() {
    disableDeviceLock();
    setLockOn(false);
    toast.info("قفل دستگاه");
  }

  return (
    <section className="panel">
      <div className="card auth-card">
        <div className="mode-tabs">
          <button
            type="button"
            className={mode === "login" ? "chip active" : "chip"}
            onClick={() => setMode("login")}
          >
            ورود
          </button>
          <button
            type="button"
            className={mode === "register" ? "chip active" : "chip"}
            onClick={() => setMode("register")}
          >
            ثبت‌نام
          </button>
        </div>
        <label>
          نام کاربری
          <input
            className="ltr"
            value={username}
            onChange={(e) => setUsername(e.target.value.trim())}
            maxLength={32}
            autoComplete="username"
            spellCheck={false}
          />
        </label>
        <label>
          رمز عبور
          <input
            className="ltr"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            maxLength={64}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
          />
        </label>
        {mode === "register" && (
          <label>
            تکرار رمز
            <input
              className="ltr"
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              maxLength={64}
              autoComplete="new-password"
            />
          </label>
        )}
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? "…" : mode === "register" ? "ثبت‌نام" : "ورود"}
        </button>
      </div>

      {session && (
        <div className="card lock-card">
          <div className="row-between">
            <span>قفل دستگاه</span>
            <span className="muted">{lockOn ? "on" : lockReady ? "off" : "—"}</span>
          </div>
          <div className="row-actions">
            {!lockOn ? (
              <button
                type="button"
                className="chip"
                disabled={!lockReady}
                onClick={() => void onEnableLock()}
              >
                فعال
              </button>
            ) : (
              <button type="button" className="chip" onClick={onDisableLock}>
                غیرفعال
              </button>
            )}
            <button type="button" className="chip" onClick={() => void refreshLock()}>
              بررسی
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
